import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { JournalEntry, MachinePhoto } from './machining-journal.model';

@Injectable({ providedIn: 'root' })
export class MachiningJournalService {
  private injector = inject(EnvironmentInjector);

  constructor(
    private db: AngularFireDatabase,
    private storage: AngularFireStorage
  ) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  // ── Journal Entries ──────────────────────────────────────────────────

  getEntries(userId: string): Observable<JournalEntry[]> {
    return this.inCtx(() => this.db
      .list<JournalEntry>(`machining-journal/${userId}`)
      .valueChanges()
      .pipe(map(entries => entries.sort((a, b) => b.date.localeCompare(a.date)))));
  }

  addEntry(userId: string, entry: Omit<JournalEntry, 'id' | 'createdAt'>): Promise<string> {
    const id = uuidv4();
    const full: JournalEntry = { ...entry, id, createdAt: Date.now() };
    return this.inCtx(() => this.db
      .object(`machining-journal/${userId}/${id}`)
      .set(full))
      .then(() => id);
  }

  updateEntry(userId: string, entry: JournalEntry): Promise<void> {
    return this.inCtx(() => this.db
      .object(`machining-journal/${userId}/${entry.id}`)
      .update({ ...entry, updatedAt: Date.now() }));
  }

  async deleteEntry(userId: string, entry: JournalEntry, photos: MachinePhoto[]): Promise<void> {
    await Promise.all(photos.map(p => this.deletePhoto(userId, p)));
    return this.inCtx(() => this.db.object(`machining-journal/${userId}/${entry.id}`).remove());
  }

  // ── Photos ────────────────────────────────────────────────────────────

  getPhotosForEntry(userId: string, entryId: string): Observable<MachinePhoto[]> {
    return this.inCtx(() => this.db
      .list<MachinePhoto>(`machining-photos/${userId}`, ref =>
        ref.orderByChild('journalEntryId').equalTo(entryId)
      )
      .valueChanges()
      .pipe(map(photos => photos.sort((a, b) => a.uploadedAt - b.uploadedAt))));
  }

  getAllPhotos(userId: string): Observable<MachinePhoto[]> {
    return this.inCtx(() => this.db
      .list<MachinePhoto>(`machining-photos/${userId}`)
      .valueChanges()
      .pipe(map(photos => photos.sort((a, b) => b.uploadedAt - a.uploadedAt))));
  }

  async uploadPhoto(
    userId: string,
    file: File,
    journalEntryId: string,
    journalDate: string,
    caption?: string
  ): Promise<MachinePhoto> {
    const id = uuidv4();
    const ext = file.name.split('.').pop() || 'jpg';
    const storagePath = `machining/${userId}/photos/${id}.${ext}`;

    const ref = this.storage.ref(storagePath);
    await ref.put(file);
    const downloadUrl: string = await ref.getDownloadURL().toPromise();

    const photo: MachinePhoto = {
      id,
      journalEntryId,
      journalDate,
      storagePath,
      downloadUrl,
      uploadedAt: Date.now(),
      ...(caption ? { caption } : {})
    };

    await this.inCtx(() => this.db.object(`machining-photos/${userId}/${id}`).set(photo));
    return photo;
  }

  async deletePhoto(userId: string, photo: MachinePhoto): Promise<void> {
    await Promise.all([
      this.storage.ref(photo.storagePath).delete().toPromise().catch(() => {}),
      this.inCtx(() => this.db.object(`machining-photos/${userId}/${photo.id}`).remove())
    ]);
  }

  updatePhotoCaption(userId: string, photoId: string, caption: string): Promise<void> {
    return this.inCtx(() => this.db.object(`machining-photos/${userId}/${photoId}`).update({ caption }));
  }
}
